/**
 * useOutsideClick Hook
 * 
 * Custom hook for managing modal/dropdown visibility with outside click detection.
 * Detects clicks outside a referenced element and toggles open state automatically.
 * Commonly used for closing modals and dropdowns when user clicks outside their boundaries.
 * 
 * @returns {Object} Object with ref element reference, isOpen state boolean, and toggleNav function
 * @returns {React.RefObject} returns.ref - Ref to DOM element for tracking clicks
 * @returns {boolean} returns.isOpen - Current open/closed state
 * @returns {Function} returns.toggleNav - Function to toggle open state
 */
// hook for handling opening and closing of modals also allows outside clicks to close modal

import { useEffect, useRef, useState } from "react";

const useOutsideClick = () => {
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const toggleNav = () => setOpen(!isOpen);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!ref.current.contains(e.target)) {
        toggleNav();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  return { ref, isOpen, toggleNav };
};

export default useOutsideClick;
